import db, { cacheTasksMerged } from './offlineDb'
import * as taskService from './taskService'

export const OFFLINE_TASK_CAP = 500
export const OFFLINE_PAGE_SIZE = 100
export const OFFLINE_MAX_PAGES = 5
const PREFETCH_SESSION_KEY = 'bp:offline-task-prefetch'

const fetchOpenTasks = async (params, maxPages, seen, collected) => {
  let page = 1
  let lastPage = 1
  do {
    if (collected.length >= OFFLINE_TASK_CAP || maxPages <= 0) return maxPages
    const result = await taskService.getTasks({ ...params, page, limit: OFFLINE_PAGE_SIZE, sort: 'id', 'filter[open]': 1 })
    maxPages -= 1
    const rows = result.data?.data || []
    lastPage = result.data?.meta?.lastPage || (rows.length === OFFLINE_PAGE_SIZE ? page + 1 : page)
    rows.forEach(task => {
      if (collected.length < OFFLINE_TASK_CAP && !seen.has(task.id)) {
        seen.add(task.id)
        collected.push(task)
      }
    })
    page += 1
  } while (page <= lastPage)
  return maxPages
}

export const fetchOfflineTasks = async ({ window = 'week' } = {}) => {
  const seen = new Set()
  const collected = []
  const assignedBudget = Math.min(2, OFFLINE_MAX_PAGES)
  const unusedAssigned = await fetchOpenTasks({ 'filter[assigned_to]': 'me', window: 'all' }, assignedBudget, seen, collected)
  await fetchOpenTasks({ window }, OFFLINE_MAX_PAGES - assignedBudget + unusedAssigned, seen, collected)
  return collected
}

const scheduleIdle = fn => {
  if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(() => fn(), { timeout: 10000 })
  } else {
    setTimeout(fn, 2000)
  }
}

const alreadyPrefetched = () => {
  try { return sessionStorage.getItem(PREFETCH_SESSION_KEY) === '1' } catch { return false }
}

const markPrefetched = () => {
  try { sessionStorage.setItem(PREFETCH_SESSION_KEY, '1') } catch { }
}

let scheduled = false

export const resetTaskPrefetchSchedule = () => {
  scheduled = false
  try { sessionStorage.removeItem(PREFETCH_SESSION_KEY) } catch { }
}

export const scheduleTaskPrefetch = (options = {}) => {
  if (scheduled || alreadyPrefetched()) return false
  if (typeof navigator !== 'undefined' && navigator.connection?.saveData) return false
  scheduled = true
  scheduleIdle(() => {
    prefetchTaskData(options)
      .then(markPrefetched)
      .catch(() => { scheduled = false })
  })
  return true
}

export const prefetchTaskData = async (options = {}) => {
  try {
    const allTasks = await fetchOfflineTasks(options)

    if (allTasks.length > 0) {
      // Protect offline-modified tasks from being overwritten by server data
      const offlineTaskIds = new Set()
      await db.tasks.filter(t => t._offline === true).each(t => offlineTaskIds.add(t.id))
      const safeTasks = allTasks.filter(t => !offlineTaskIds.has(t.id))
      if (safeTasks.length > 0) {
        await cacheTasksMerged(safeTasks)
      }
    }

    // Fetch projects
    const projectsResult = await taskService.getProjects()
    const projects = projectsResult.data?.data || []
    if (projects.length > 0) {
      await db.projects.bulkPut(projects)
    }

    // Fetch teams
    const teamsResult = await taskService.getTeams()
    const teams = teamsResult.data?.data || []
    if (teams.length > 0) {
      await db.teams.bulkPut(teams)
    }

    // Fetch form templates
    const templatesResult = await taskService.getFormTemplates({ limit: 200 })
    const templates = templatesResult.data?.data || []
    if (templates.length > 0) {
      await db.formTemplates.bulkPut(templates)
    }

    console.log(`Prefetched for offline: ${allTasks.length} tasks, ${projects.length} projects, ${teams.length} teams, ${templates.length} templates`)
    return { tasks: allTasks.length, projects: projects.length, teams: teams.length, templates: templates.length }
  } catch (error) {
    console.warn('Offline prefetch failed:', error)
    throw error
  }
}
